import { useCallback, useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, MessageSquarePlus, RefreshCw, Send } from "lucide-react";

function Thread({ messages, loading }) {
  if (loading) {
    return <div className="flex justify-center py-4"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div>;
  }
  if (!messages.length) {
    return <p className="py-3 text-center text-xs text-muted-foreground" data-testid="sms-thread-empty">No texts with this number yet.</p>;
  }
  return (
    <div className="max-h-52 space-y-2 overflow-y-auto rounded-md border border-border bg-secondary/20 p-3" data-testid="sms-thread">
      {messages.map((m, i) => (
        <div key={i} className={`flex ${m.direction === "outbound" ? "justify-end" : "justify-start"}`}>
          <div className={`max-w-[80%] rounded-lg px-3 py-1.5 text-xs leading-relaxed ${
            m.direction === "outbound"
              ? "bg-[hsl(var(--primary))]/90 text-white"
              : "border border-border bg-card text-foreground"}`}>
            <div>{m.text}</div>
            <div className={`mt-0.5 text-[10px] ${m.direction === "outbound" ? "text-white/60" : "text-muted-foreground"}`}>
              {m.at ? new Date(m.at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : ""}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function SmsCustomerDialog({ quote, trackingUrl, open, onOpenChange }) {
  const [phone, setPhone] = useState(quote?.dest_contact_phone || "");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [thread, setThread] = useState([]);
  const [threadLoading, setThreadLoading] = useState(false);

  const loadThread = useCallback(async (target) => {
    if (!quote?.id || !(target || "").trim()) return;
    setThreadLoading(true);
    try {
      const { data } = await api.get(`/quotes/${quote.id}/sms-thread`, { params: { phone: target } });
      setThread(data.messages || []);
    } catch {
      setThread([]);
    } finally {
      setThreadLoading(false);
    }
  }, [quote?.id]);

  useEffect(() => {
    if (open) loadThread(phone);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!quote) return null;

  const qn = quote.quote_number;
  const pickup = (quote.pickup_date || "").slice(0, 10);
  const eta = (quote.gtz_estimated_delivery || quote.tracking?.estimated_delivery || "").slice(0, 10);
  const link = trackingUrl ? ` Track live: ${trackingUrl}` : "";
  const templates = [
    { label: "Pickup reminder", text: `PHS Freight: reminder — your shipment ${qn} is scheduled for pickup${pickup ? ` on ${pickup}` : " soon"}. Please have the freight ready at the dock.${link}` },
    { label: "On the way", text: `PHS Freight: your shipment ${qn} is on the way${eta ? ` — estimated delivery ${eta}` : ""}.${link}` },
    { label: "Delay heads-up", text: `PHS Freight: heads-up — your shipment ${qn} may see a slight delay. We're monitoring it closely and will keep you posted.${link}` },
  ];

  const send = async () => {
    setBusy(true);
    try {
      await api.post(`/quotes/${quote.id}/sms`, { phone, message });
      toast.success("Text sent");
      setMessage("");
      loadThread(phone);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="sms-customer-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquarePlus className="h-5 w-5 text-[hsl(var(--primary))]" /> Text customer — {qn}
          </DialogTitle>
          <DialogDescription>Sends from the PHS GoHighLevel number. Keep it short — it's a real SMS.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <label className="label-caps text-muted-foreground">Mobile number</label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(847) 555-1234"
                className="mt-1 h-10" data-testid="sms-phone-input" />
            </div>
            <Button variant="outline" size="icon" className="h-10 w-10 shrink-0" onClick={() => loadThread(phone)}
              disabled={threadLoading} title="Refresh conversation" data-testid="sms-thread-refresh">
              <RefreshCw className={`h-4 w-4 ${threadLoading ? "animate-spin" : ""}`} />
            </Button>
          </div>
          <Thread messages={thread} loading={threadLoading} />
          <div className="flex flex-wrap gap-1.5">
            {templates.map((t) => (
              <button key={t.label} onClick={() => setMessage(t.text)} data-testid={`sms-template-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="rounded-full border border-border bg-secondary/40 px-3 py-1 text-xs font-semibold text-muted-foreground transition-colors hover:border-[hsl(var(--primary))]/50 hover:text-foreground">
                {t.label}
              </button>
            ))}
          </div>
          <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4}
            placeholder="Type a message or pick a template above…" data-testid="sms-message-input" />
          <div className="text-right text-[11px] text-muted-foreground">{message.length} chars</div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={send} disabled={busy || !phone.trim() || !message.trim()} data-testid="sms-send-btn" className="gap-1.5">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send text
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
